import { useMemo } from "react";
import type { ReactNode } from "react";
import { isSafeHref, parseMarkdown } from "@astrum/shared";
import type { MdBlock, MdInline } from "@astrum/shared";
import { useUserGroups } from "../../lib/user-groups.js";
import type { UserGroup } from "../../lib/user-groups.js";

/**
 * A message body: its markdown rendered, its mentions as chips.
 *
 * The stored text is markdown and never HTML, and it is turned into React
 * elements here from the shared parser's tree — never into an HTML string —
 * so whatever somebody types, `<script>` included, can only ever come out as
 * text. A link is a link only when its address is http, https or mailto; the
 * parser already refuses the rest, and the check is repeated at the anchor so
 * the guarantee does not rest on one function.
 *
 * A mention's stored form is `@[Name](id)` — a reference, not a name — so a
 * mention survives somebody being renamed. Being named yourself, or being in a
 * group that was named, is marked differently, because the reason to scan a
 * busy channel is to find where somebody wanted *you*.
 */

const BROADCAST = new Set(["channel", "here", "everyone"]);

type RenderContext = { viewerId: string | undefined; groups: Map<string, UserGroup> };

function renderInlines(nodes: MdInline[], ctx: RenderContext): ReactNode[] {
  return nodes.map((node, index) => {
    switch (node.type) {
      case "text":
        return node.text;
      case "break":
        return <br key={index} />;
      case "strong":
        return <strong key={index}>{renderInlines(node.children, ctx)}</strong>;
      case "em":
        return <em key={index}>{renderInlines(node.children, ctx)}</em>;
      case "strike":
        return <s key={index}>{renderInlines(node.children, ctx)}</s>;
      case "code":
        return (
          <code key={index} className="chat-md__code">
            {node.text}
          </code>
        );
      case "link":
        if (!isSafeHref(node.href)) return <span key={index}>{renderInlines(node.children, ctx)}</span>;
        return (
          <a key={index} href={node.href} target="_blank" rel="noopener noreferrer" className="linked-text__link">
            {renderInlines(node.children, ctx)}
          </a>
        );
      case "mention":
        return (
          <span key={index} className={`chat-mention ${node.userId === ctx.viewerId ? "is-you" : ""}`}>
            @{node.label}
          </span>
        );
      case "handle": {
        const handle = node.name.toLowerCase();
        if (BROADCAST.has(handle)) {
          return (
            <span key={index} className="chat-mention is-you">
              @{node.name}
            </span>
          );
        }
        const group = ctx.groups.get(handle);
        // An `@word` that is no group is somebody typing an at sign, not a
        // mention, and reads as the text it is.
        if (!group) return `@${node.name}`;
        const mine = ctx.viewerId !== undefined && group.memberIds.includes(ctx.viewerId);
        return (
          <span key={index} className={`chat-mention ${mine ? "is-you" : ""}`} title={group.name}>
            @{node.name}
          </span>
        );
      }
    }
  });
}

function renderBlocks(blocks: MdBlock[], ctx: RenderContext): ReactNode[] {
  return blocks.map((block, index) => {
    switch (block.type) {
      case "heading": {
        // Rich mode only (project docs); the level maps to h2..h6 so a page's
        // own title stays the one h1.
        const Tag = `h${Math.min(block.level + 1, 6)}` as "h2" | "h3" | "h4" | "h5" | "h6";
        return (
          <Tag key={index} className="chat-md__h">
            {renderInlines(block.children, ctx)}
          </Tag>
        );
      }
      case "paragraph":
        return (
          <p key={index} className="chat-md__p">
            {renderInlines(block.children, ctx)}
          </p>
        );
      case "codeBlock":
        return (
          <pre key={index} className="chat-md__pre">
            <code>{block.text}</code>
          </pre>
        );
      case "quote":
        return (
          <blockquote key={index} className="chat-md__quote">
            {renderBlocks(block.children, ctx)}
          </blockquote>
        );
      case "list": {
        const items = block.items.map((item, i) => {
          const check = block.checks?.[i];
          return check === undefined || check === null ? (
            <li key={i}>{renderBlocks(item, ctx)}</li>
          ) : (
            // A task item shows its state read-only; the text is what is edited.
            <li key={i} className="chat-md__task">
              <input type="checkbox" checked={check} readOnly disabled aria-label={check ? "Done" : "Not done"} />
              <div>{renderBlocks(item, ctx)}</div>
            </li>
          );
        });
        return block.ordered ? (
          <ol key={index} className="chat-md__list" start={block.start === 1 ? undefined : block.start}>
            {items}
          </ol>
        ) : (
          <ul key={index} className="chat-md__list">
            {items}
          </ul>
        );
      }
    }
  });
}

export function MessageBody({
  body,
  viewerId,
  rich = false,
}: {
  body: string;
  viewerId: string | undefined;
  /** Headings and task lists too — for project docs, not chat. */
  rich?: boolean;
}) {
  const groups = useUserGroups();
  const byHandle = useMemo(() => new Map(groups.map((g) => [g.handle.toLowerCase(), g])), [groups]);
  const blocks = useMemo(() => parseMarkdown(body, { rich }), [body, rich]);

  // The common case — one paragraph of plain text — stays a bare text node
  // rather than a wrapper per message.
  const only = blocks.length === 1 ? blocks[0] : undefined;
  if (!rich && only?.type === "paragraph" && only.children.length === 1 && only.children[0]!.type === "text") {
    return <>{only.children[0]!.text}</>;
  }

  return <div className={rich ? "chat-md chat-md--doc" : "chat-md"}>{renderBlocks(blocks, { viewerId, groups: byHandle })}</div>;
}
